import { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "@localization/useTranslation";
import DocumentPreviewContent from "@common/components/documentPreview/DocumentPreviewContent";
import { DocumentPreviewTitle } from "@common/components/documentPreview/DocumentPreviewHeader";
import { useDocumentPreviewStyles } from "@common/components/documentPreview/DocumentPreview.styles";
import { IDocumentPreviewToolbarAction, PreviewableDocument } from "@common/components/documentPreview/DocumentPreview.types";
import { aiMagicDustIcon } from "@common/icons";
import { useObligationExtractionAffordance } from "../../../contract/contract/contractDocument/obligationExtraction/useObligationExtractionAffordance";
import PaneLayout from "../paneLayout/PaneLayout";
import { usePane } from "../paneLayout/usePane";
import { definePane } from "../definePane";
import { useStageDocumentOverlay } from "./stageDocumentOverlay";
import { getReviewedDocumentId } from "./documentReviewPane";
import { usePanes, usePaneSlots } from "../usePanes";
import ObligationExtractionPane from "../../../contract/contract/contractDocument/obligationExtraction/ObligationExtractionPane";
export interface DocumentExtractionOffer {
    contractId: string;
    /** Where the review's "go to obligations" lands once it has closed both panes. */
    onGoToObligations?: () => void;
}

export interface DocumentPaneProps {
    documents: Array<PreviewableDocument>;
    initialDocumentId?: string;
    initialPage?: number;
    initialSearchText?: string;
    initialMatchSnippet?: string;
    /** Text known at open time to find and highlight. A review in the rail supplies a live one instead. */
    citationChunkContent?: string;
    /** Translation key for the banner shown when that text is not found; the pane resolves it. */
    citationNotFoundKey?: string;
    /** Keeps a scanned pdf showing as the scan. Left out, the pane decides from whether a review is under way. */
    keepScannedAppearance?: boolean;
    /** Puts the "find obligations" action in the toolbar, which opens a review in the rail beside the document. */
    offerObligationExtraction?: DocumentExtractionOffer;
}

/**
 * A document on the stage. The viewer the fullscreen previewer composes, minus the Dialog, plus whatever
 * a review in the rail says about the document currently on show.
 */
function DocumentPane(props: DocumentPaneProps) {
    const { translateString } = useTranslation();
    const previewStyles = useDocumentPreviewStyles();
    const { close, slot } = usePane();
    const { open, closeSlot, requestCloseSlot } = usePanes();
    const { rightPane } = usePaneSlots();
    const overlay = useStageDocumentOverlay();
    const affordance = useObligationExtractionAffordance();
    const [currentDocument, setCurrentDocument] = useState<PreviewableDocument | undefined>(undefined);

    // The content's effect keys on this callback's identity, so it has to stay stable across renders.
    const handleCurrentDocumentChange = useCallback((document: PreviewableDocument | undefined) => setCurrentDocument(document), []);

    const reviewDocumentId = getReviewedDocumentId(rightPane);
    const isObligationReviewOpen = rightPane?.definition === ObligationExtractionPane;
    const hasReviewForCurrentDocument = reviewDocumentId !== undefined && currentDocument !== undefined && reviewDocumentId === currentDocument.documentId;

    // Paging to another document would leave the review in the rail working on clauses that are no longer
    // on screen, and the review belongs to the document being left, so it goes rather than lingering.
    // Without asking: the confirmation is for the close button, and this has always been silent.
    useEffect(() => {
        if (reviewDocumentId && currentDocument && reviewDocumentId !== currentDocument.documentId) {
            closeSlot("right");
        }
    }, [reviewDocumentId, currentDocument, closeSlot]);

    const overlayApplies = overlay !== undefined && currentDocument !== undefined && overlay.documentId === currentDocument.documentId;
    const overlayExtractedText = overlayApplies ? overlay.extractedText : undefined;

    // The action is an icon with no label, so the blocked reason on its own would tell the reader that a
    // feature they cannot see the name of is unavailable. The label carries the name the button cannot.
    const blockedTranslationKey = affordance.blockedTranslationKey ?? (currentDocument?.isTooLargeForAi ? "textExtractionErrorFileTooLarge" : undefined);
    const blockedTooltip = blockedTranslationKey ? `${translateString("findObligationsWithAi")}: ${translateString(blockedTranslationKey)}` : undefined;

    function buildExtractionAction(offer: DocumentExtractionOffer): IDocumentPreviewToolbarAction {
        return {
            icon: aiMagicDustIcon,
            tooltip: blockedTooltip ?? translateString("findObligationsWithAi"),
            disabled: blockedTooltip !== undefined || currentDocument === undefined,
            onClick: () => {
                if (isObligationReviewOpen && hasReviewForCurrentDocument) {
                    requestCloseSlot("right");
                    return;
                }
                open(ObligationExtractionPane, {
                    contractId: offer.contractId,
                    documentId: currentDocument!.documentId,
                    onGoToObligations: offer.onGoToObligations,
                });
            },
        };
    }

    const extractionOffer = props.offerObligationExtraction;
    const toolbarAction = extractionOffer && affordance.showObligationExtraction ? buildExtractionAction(extractionOffer) : undefined;

    return (
        <PaneLayout
            noContentPadding
            header={
                <DocumentPreviewTitle
                    entityType={currentDocument?.entityType}
                    entityId={currentDocument?.entityId}
                    entityTitle={currentDocument?.entityTitle}
                    fileName={currentDocument?.name ?? ""}
                    align={slot === "right" ? "start" : "center"}
                    onEntityLinkNavigate={close}
                />
            }
        >
            <Box sx={previewStyles.viewerColumn}>
                <DocumentPreviewContent
                    open
                    documents={props.documents}
                    initialDocumentId={props.initialDocumentId}
                    initialPage={props.initialPage}
                    initialSearchText={props.initialSearchText}
                    initialMatchSnippet={props.initialMatchSnippet}
                    // Changing this as the review pages re-scrolls and re-marks the viewer without remounting it.
                    citationChunkContent={overlayApplies ? overlay.citationChunkContent : props.citationChunkContent}
                    citationNotFoundText={
                        overlayApplies
                            ? translateString("clauseNotFoundInDocument")
                            : props.citationNotFoundKey
                              ? translateString(props.citationNotFoundKey)
                              : undefined
                    }
                    // Previewing a document is for reading it, and a scan should look like the scan until a
                    // review needs its text: one in the rail for this document, or one that read an unstored
                    // document itself and handed the text over.
                    keepScannedAppearance={props.keepScannedAppearance ?? !(hasReviewForCurrentDocument || overlayExtractedText !== undefined)}
                    localExtractedText={overlayExtractedText}
                    // Only while a review is under way: without it there is nowhere for a picked clause to go.
                    textSelection={overlayApplies ? overlay.textSelection : undefined}
                    toolbarAction={toolbarAction}
                    onCurrentDocumentChange={handleCurrentDocumentChange}
                />
            </Box>
        </PaneLayout>
    );
}

export default definePane({ slot: "stage", component: DocumentPane });
